import React, { useEffect, useRef, useState } from "react";
import { AutoComplete, Button, Input, Space, message } from "antd";
import { useTranslation } from "react-i18next";

import {
    searchTiandituLocationSuggestions,
    type TiandituLocationSuggestion,
} from "../../services/tiandituLocationService";
import { loadTiandituApi } from "../../utils/tianditu";
import { normalizeSearchText } from "../../utils/searchText";

interface TiandituLngLat {
    lng: number;
    lat: number;
}

type TiandituMarker = Record<string, unknown>;

interface TiandituMap {
    centerAndZoom(point: TiandituLngLat, zoom: number): void;
    addControl(control: unknown): void;
    addOverLay(overlay: TiandituMarker): void;
    removeOverLay(overlay: TiandituMarker): void;
    addEventListener(
        eventName: "click",
        handler: (event: { lnglat: TiandituLngLat }) => void,
    ): void;
    panTo?(point: TiandituLngLat, zoom?: number): void;
    enableDrag?(): void;
    enableScrollWheelZoom?(): void;
    enableDoubleClickZoom?(): void;
    enableKeyboard?(): void;
}

interface TiandituNamespace {
    Map: new (container: HTMLDivElement) => TiandituMap;
    LngLat: new (lng: number, lat: number) => TiandituLngLat;
    Marker: new (point: TiandituLngLat) => TiandituMarker;
    Control: {
        MapType: new () => unknown;
        Zoom: new () => unknown;
    };
}

declare global {
    interface Window {
        T: TiandituNamespace;
    }
}

interface MapSelectorProps {
    currentLng: number | null;
    currentLat: number | null;
    onLocationSelected: (lng: number, lat: number) => void;
}

const DEFAULT_CENTER_LNG = 113.70403;
const DEFAULT_CENTER_LAT = 23.01841;
const SEARCH_RESULT_LIMIT = 10;

interface LocationSuggestion {
    value: string;
    label: React.ReactNode;
    suggestion: TiandituLocationSuggestion;
}

const enableMapInteractions = (mapInstance: TiandituMap) => {
    mapInstance.enableDrag?.();
    mapInstance.enableScrollWheelZoom?.();
    mapInstance.enableDoubleClickZoom?.();
    mapInstance.enableKeyboard?.();
};

const parseSuggestionLngLat = (
    suggestion: TiandituLocationSuggestion,
): TiandituLngLat => ({
    lng: suggestion.lng,
    lat: suggestion.lat,
});

const getSuggestionTitle = (suggestion: TiandituLocationSuggestion): string =>
    suggestion.name;

const getSuggestionDescription = (
    suggestion: TiandituLocationSuggestion,
): string =>
    [
        suggestion.address,
        suggestion.province,
        suggestion.city,
        suggestion.county,
    ]
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
        .join(" / ");

const isValidCoordinate = (value: number | null): value is number =>
    value !== null && Number.isFinite(value);

const buildLocationSuggestions = (
    suggestions: TiandituLocationSuggestion[],
): LocationSuggestion[] =>
    suggestions.slice(0, SEARCH_RESULT_LIMIT).map((suggestion, index) => {
        const title = getSuggestionTitle(suggestion);
        const description = getSuggestionDescription(suggestion);
        const coordinates = `${suggestion.lng},${suggestion.lat}`;

        return {
            value: `${index}-${suggestion.id}-${coordinates}`,
            label: (
                <div>
                    <div>{title}</div>
                    {description ? (
                        <div style={{ color: "#8c8c8c", fontSize: 12 }}>
                            {description}
                        </div>
                    ) : null}
                </div>
            ),
            suggestion,
        };
    });

const MapSelector: React.FC<MapSelectorProps> = ({
    currentLng,
    currentLat,
    onLocationSelected,
}) => {
    const { t } = useTranslation(["device", "common"]);
    const [messageApi, contextHolder] = message.useMessage();
    const tRef = useRef(t);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<TiandituMap | null>(null);
    const markerRef = useRef<TiandituMarker | null>(null);
    const [map, setMap] = useState<TiandituMap | null>(null);
    const [selectedLng, setSelectedLng] = useState<number | null>(currentLng);
    const [selectedLat, setSelectedLat] = useState<number | null>(currentLat);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
    const suggestionSearchTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const suggestionSearchRequestRef = useRef(0);

    useEffect(() => {
        tRef.current = t;
    }, [t]);

    useEffect(() => {
        setSelectedLng(currentLng);
        setSelectedLat(currentLat);
    }, [currentLng, currentLat]);

    const replaceMarker = React.useCallback(
        (point: TiandituLngLat, targetMap?: TiandituMap | null) => {
            const activeMap = targetMap || mapInstanceRef.current;
            if (!activeMap) {
                return;
            }

            if (markerRef.current) {
                activeMap.removeOverLay(markerRef.current);
            }

            const nextMarker = new window.T.Marker(point);
            activeMap.addOverLay(nextMarker);
            markerRef.current = nextMarker;
        },
        [],
    );

    useEffect(() => {
        let isCancelled = false;
        const currentMapContainer = mapContainerRef.current;

        loadTiandituApi()
            .then(() => {
                if (isCancelled || !currentMapContainer) {
                    return;
                }

                currentMapContainer.innerHTML = "";

                const mapInstance = new window.T.Map(currentMapContainer);
                const centerPoint = new window.T.LngLat(
                    currentLng ?? DEFAULT_CENTER_LNG,
                    currentLat ?? DEFAULT_CENTER_LAT,
                );

                mapInstance.centerAndZoom(centerPoint, 15);
                mapInstance.addControl(new window.T.Control.MapType());
                mapInstance.addControl(new window.T.Control.Zoom());
                enableMapInteractions(mapInstance);

                mapInstanceRef.current = mapInstance;
                setMap(mapInstance);

                if (currentLng !== null && currentLat !== null) {
                    replaceMarker(
                        new window.T.LngLat(currentLng, currentLat),
                        mapInstance,
                    );
                }

                mapInstance.addEventListener("click", (event) => {
                    const lnglat = event.lnglat;
                    setSelectedLng(lnglat.lng);
                    setSelectedLat(lnglat.lat);
                    replaceMarker(lnglat, mapInstance);
                });
            })
            .catch((error) => {
                console.error("Error loading Tianditu API:", error);
                const errorMessage =
                    error instanceof Error &&
                    (error.message.includes("TIANDITU_BROWSER_TOKEN") ||
                        error.message.includes("VITE_TIANDITU_TK"))
                        ? tRef.current("device:mapModal.missingToken")
                        : tRef.current("device:mapModal.mapLoadFailed");

                messageApi.error(errorMessage);
            });

        return () => {
            isCancelled = true;
            if (suggestionSearchTimerRef.current) {
                clearTimeout(suggestionSearchTimerRef.current);
                suggestionSearchTimerRef.current = null;
            }
            markerRef.current = null;
            mapInstanceRef.current = null;
            setMap(null);
            if (currentMapContainer) {
                currentMapContainer.innerHTML = "";
            }
        };
    }, [currentLat, currentLng, messageApi, replaceMarker]);

    const handleConfirm = () => {
        if (selectedLng !== null && selectedLat !== null) {
            onLocationSelected(selectedLng, selectedLat);
            return;
        }

        messageApi.warning(t("device:mapModal.selectLocationRequired"));
    };

    const handleManualInput = () => {
        if (!isValidCoordinate(selectedLng)) {
            messageApi.warning(t("device:mapModal.invalidLongitude"));
            return;
        }

        if (!isValidCoordinate(selectedLat)) {
            messageApi.warning(t("device:mapModal.invalidLatitude"));
            return;
        }

        const activeMap = map || mapInstanceRef.current;
        if (!activeMap) {
            messageApi.error(t("device:mapModal.mapNotInitialized"));
            return;
        }

        const point = new window.T.LngLat(selectedLng, selectedLat);
        replaceMarker(point, activeMap);
        activeMap.panTo?.(point, 16);
        enableMapInteractions(activeMap);
    };

    const getActiveMap = (): TiandituMap | null =>
        map || mapInstanceRef.current;

    const canUseMapLocation = (activeMap: TiandituMap | null): boolean => {
        if (!activeMap) {
            messageApi.error(t("device:mapModal.mapNotInitialized"));
            return false;
        }

        return true;
    };

    const applySuggestionLocation = (
        suggestion: TiandituLocationSuggestion,
        activeMap: TiandituMap,
        showSuccess = true,
    ): boolean => {
        const lnglat = parseSuggestionLngLat(suggestion);
        const point = new window.T.LngLat(lnglat.lng, lnglat.lat);
        activeMap.centerAndZoom(point, 16);
        replaceMarker(point, activeMap);
        enableMapInteractions(activeMap);
        setSelectedLng(lnglat.lng);
        setSelectedLat(lnglat.lat);

        if (showSuccess) {
            messageApi.success(
                t("device:mapModal.locationSelected", {
                    name: getSuggestionTitle(suggestion),
                }),
            );
        }

        return true;
    };

    const searchLocationPois = async (
        keyword: string,
        showError = true,
    ): Promise<TiandituLocationSuggestion[]> => {
        const normalizedKeyword = normalizeSearchText(keyword);
        if (!normalizedKeyword) {
            return [];
        }

        try {
            return await searchTiandituLocationSuggestions(normalizedKeyword, {
                limit: SEARCH_RESULT_LIMIT,
            });
        } catch (error) {
            console.error("Search error:", error);
            if (showError) {
                messageApi.error(t("device:mapModal.searchFailed"));
            }
            return [];
        }
    };

    const handleSearch = (keyword: string) => {
        const normalizedKeyword = normalizeSearchText(keyword);
        if (!normalizedKeyword) {
            return;
        }

        void searchLocationPois(normalizedKeyword).then((items) => {
            setSuggestions(buildLocationSuggestions(items));

            if (items.length === 0) {
                messageApi.warning(t("device:mapModal.noSearchResults"));
            }
        });
    };

    const handleSearchKeywordChange = (value: string) => {
        setSearchKeyword(value);

        if (suggestionSearchTimerRef.current) {
            clearTimeout(suggestionSearchTimerRef.current);
        }

        const normalizedKeyword = normalizeSearchText(value);
        if (!normalizedKeyword) {
            suggestionSearchRequestRef.current += 1;
            setSuggestions([]);
            return;
        }

        const requestId = suggestionSearchRequestRef.current + 1;
        suggestionSearchRequestRef.current = requestId;
        suggestionSearchTimerRef.current = setTimeout(() => {
            void searchLocationPois(normalizedKeyword, false).then((items) => {
                if (suggestionSearchRequestRef.current !== requestId) {
                    return;
                }

                setSuggestions(buildLocationSuggestions(items));
            });
        }, 300);
    };

    const handleSuggestionSelect = (
        _value: string,
        option: LocationSuggestion,
    ) => {
        const activeMap = getActiveMap();
        if (!canUseMapLocation(activeMap)) {
            return;
        }

        const title = getSuggestionTitle(option.suggestion);
        setSearchKeyword(title);
        applySuggestionLocation(option.suggestion, activeMap!);
    };

    return (
        <div style={{ position: "relative" }}>
            {contextHolder}
            <div style={{ marginBottom: 16 }}>
                <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="middle"
                >
                    <AutoComplete
                        value={searchKeyword}
                        options={suggestions}
                        onChange={handleSearchKeywordChange}
                        onSelect={handleSuggestionSelect}
                        style={{ width: "100%" }}
                    >
                        <Input.Search
                            placeholder={t("device:mapModal.searchPlaceholder")}
                            enterButton={t("device:mapModal.searchButton")}
                            onSearch={handleSearch}
                            allowClear
                        />
                    </AutoComplete>
                    <Space>
                        <span>{t("device:mapModal.longitude")}</span>
                        <Input
                            type="number"
                            value={selectedLng ?? ""}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                                const nextValue = event.target.value;
                                setSelectedLng(
                                    nextValue === ""
                                        ? null
                                        : parseFloat(nextValue),
                                );
                            }}
                            style={{ width: 140 }}
                            placeholder={t("device:mapModal.longitudePlaceholder")}
                        />
                        <span>{t("device:mapModal.latitude")}</span>
                        <Input
                            type="number"
                            value={selectedLat ?? ""}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                                const nextValue = event.target.value;
                                setSelectedLat(
                                    nextValue === ""
                                        ? null
                                        : parseFloat(nextValue),
                                );
                            }}
                            style={{ width: 140 }}
                            placeholder={t("device:mapModal.latitudePlaceholder")}
                        />
                        <Button onClick={handleManualInput}>
                            {t("device:mapModal.jumpToLocation")}
                        </Button>
                    </Space>
                </Space>
            </div>

            <div
                ref={mapContainerRef}
                style={{
                    width: "100%",
                    height: "450px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "8px",
                    marginBottom: 16,
                    overflow: "hidden",
                }}
            />

            <div style={{ textAlign: "right" }}>
                <Button type="primary" size="large" onClick={handleConfirm}>
                    {t("device:mapModal.confirmLocation")}
                </Button>
            </div>
        </div>
    );
};

export default MapSelector;
